/**
 * Raid engage — target pick, sticky/peel gates, reach helpers.
 * Phaser-free. Types shared with motion/select/kit-interp.
 */
import { raidCatalog } from "../../data/raid-catalog";
import type { RaidClassId } from "../../data/raid-catalog";
import {
  bondEngageSticky,
  dispositionOf,
  adjutantPriorityBonusOf,
} from "./raid-disposition";
import { insightRetargetHesitateMul } from "./judgment/polar";
import {
  hostileHardMinSep as hostileHardMinSepGeom,
  topThreatTarget,
  clearOrder,
  createBehaviorState,
} from "../../sim";
import type { RaidBoss, RaidUnit } from "./raid-types";
import {
  RAID_AGGRO_BOSS_ID,
  engageBossScore,
  peelInRangeBonus,
} from "./raid-aggro";
import {
  RaidEnemyTag,
  enemyEngageScoreBonus,
  enemyHasTag,
  enemyIsCombatRelevant,
  enemyWeaponReachPeelBias,
} from "./raid-enemy";
import { resolveSoftSafeFocusId } from "./judgment/kit-policy-apply";

/** Soft collective focus id — mirrors adjutant priority without importing adjutant (cycle). */
export function resolveEngagePriorityTargetId(
  allies: readonly RaidUnit[],
  enemies: readonly RaidUnit[],
  facing?: { x: number; y: number } | null,
): number | null {
  const living = enemies.filter((e) => enemyIsCombatRelevant(e));
  if (living.length === 0) return null;
  const party = allies.filter((a) => a.hp > 0 && a.team === 0);
  let ax = 0;
  let ay = 0;
  if (party.length > 0) {
    for (const a of party) {
      ax += a.x;
      ay += a.y;
    }
    ax /= party.length;
    ay /= party.length;
  } else {
    ax = living[0]!.x;
    ay = living[0]!.y;
  }
  const fx = facing?.x ?? 0;
  const fy = facing?.y ?? -1;
  const score = (e: RaidUnit): number => {
    const dx = e.x - ax;
    const dy = e.y - ay;
    const along = dx * fx + dy * fy;
    const dist = Math.hypot(dx, dy);
    return enemyEngageScoreBonus(e) + along * 1.2 - dist;
  };
  let best = living[0]!;
  let bestS = score(best);
  for (let i = 1; i < living.length; i++) {
    const e = living[i]!;
    const s = score(e);
    if (s > bestS) {
      bestS = s;
      best = e;
    }
  }
  return best.id;
}

export type EngagePickOpts = {
  facing?: { x: number; y: number } | null;
  /** Adjutant boss-meat tank — soft floor toward boss. */
  bossAnchorTankId?: number | null;
};

/** Drop behavior-select commit so the next intent is chosen from current world pose. */
export function invalidateUnitSelect(u: RaidUnit): void {
  u.brain.select = createBehaviorState();
}

/** Clear unit gesture/tactic order and force AI re-pick from current xy. */
export function clearUnitOrder(u: RaidUnit): void {
  clearOrder(u.brain.order);
  invalidateUnitSelect(u);
}

export type AiIntent = {
  tx: number;
  ty: number;
  wantAttack: boolean;
  wantHealId: number | null;
  wantInterrupt: boolean;
  /** World point to face (attack / heal / flee threat). */
  faceX: number;
  faceY: number;
  /** Hostile unit id when attacking an add; null = boss (or not attacking). */
  attackTargetId: number | null;
  /** Movement speed multiplier (gesture dash). Default 1 when omitted. */
  speedMul?: number;
};

export type EngageTarget = {
  x: number;
  y: number;
  isBoss: boolean;
  id?: number;
};

/** Role combat style from class kit (`balance.classes.*.engagement`). */
export type EngagementStyle = "hold" | "kite" | "skirmish";

export function engagementStyle(classId: RaidClassId): EngagementStyle {
  const raw = raidCatalog.balance.classes[classId].engagement;
  if (raw === "hold" || raw === "kite" || raw === "skirmish") return raw;
  if (classId === "tank") return "hold";
  if (classId === "healer") return "skirmish";
  return "kite";
}

export function fleeFromAdds(classId: RaidClassId): boolean {
  return raidCatalog.balance.classes[classId].fleeFromAdds !== false;
}

/** Class AI toggles (moved from ai-triggers.json into ai-profiles.json). */
export function classKnobs<K extends RaidClassId>(classId: K) {
  return raidCatalog.profiles.classKnobs[classId];
}

export function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

/**
 * Center→center weapon reach vs any hostile radius (boss, add, …).
 * Matches Hit gates: `d ? other.radius + attackRange`.
 */
export function hostileAttackReach(
  u: Pick<RaidUnit, "attackRange">,
  other: { radius: number },
): number {
  return other.radius + u.attackRange;
}

/** Melee / AA reach vs an add-sized radius default (center→center). */
export function addAttackReach(u: RaidUnit, addRadius = 14): number {
  return hostileAttackReach(u, { radius: addRadius });
}

export function resolveAddRadius(
  enemies: readonly RaidUnit[],
  id: number | undefined,
): number {
  if (id == null) return 14;
  const e = enemies.find((x) => x.id === id);
  return e?.radius ?? 14;
}

/**
 * Raid hard min-sep vs any hostile (boss or add) — table pad/eps + shared
 * `sim.hostileHardMinSep`. Same contract for both; never a boss-only path.
 */
export function hostileHardMinSep(
  mover: Pick<RaidUnit, "radius" | "attackRange">,
  other: { radius: number },
): number {
  const combat = raidCatalog.balance.combat as {
    bodySepPad?: number;
    bodySepReachEps?: number;
  };
  const pad = typeof combat.bodySepPad === "number" ? combat.bodySepPad : 8;
  const eps =
    typeof combat.bodySepReachEps === "number" ? combat.bodySepReachEps : 2;
  return hostileHardMinSepGeom(mover.radius, other.radius, pad, {
    attackReach: other.radius + mover.attackRange,
    reachEps: eps,
  });
}

export function faceBoss(boss: RaidBoss): Pick<AiIntent, "faceX" | "faceY"> {
  return { faceX: boss.x, faceY: boss.y };
}

export function facePoint(x: number, y: number): Pick<AiIntent, "faceX" | "faceY"> {
  return { faceX: x, faceY: y };
}

export function faceTarget(
  t: EngageTarget,
): Pick<AiIntent, "faceX" | "faceY" | "attackTargetId"> {
  return {
    faceX: t.x,
    faceY: t.y,
    attackTargetId: t.isBoss ? null : (t.id ?? null),
  };
}

/**
 * Living weaponReachPeel hostiles already inside weapon Hit gate — nearest then
 * lower HP; peelBias from enemies.json prefers packChew over casters. Survive/
 * dodge kite must not stay silent while pack AA chews. Elites excluded via tags.
 */
export function pickInWeaponReachAdd(
  u: RaidUnit,
  enemies: readonly RaidUnit[],
): RaidUnit | null {
  let best: RaidUnit | null = null;
  let bestScore = Infinity;
  for (const e of enemies) {
    if (!enemyIsCombatRelevant(e)) continue;
    if (!enemyHasTag(e, RaidEnemyTag.WeaponReachPeel)) continue;
    if (!e.combatArmed) continue;
    const d = dist(u.x, u.y, e.x, e.y);
    const reach = addAttackReach(u, e.radius);
    if (d >= reach) continue;
    const hpRatio = e.maxHp > 0 ? e.hp / e.maxHp : 1;
    const score = d + hpRatio * 28 + enemyWeaponReachPeelBias(e);
    if (score < bestScore) {
      bestScore = score;
      best = e;
    }
  }
  return best;
}

/**
 * Ranged/skirmish AA: among living adds inside weapon reach (Hit gate),
 * prefer nearest then lower HP. Boss distance is irrelevant — an add
 * sideways at the boss leash must still draw support fire.
 */
function pickRangedInRangeEngage(
  u: RaidUnit,
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
): EngageTarget {
  const bossAim: EngageTarget = { x: boss.x, y: boss.y, isBoss: true };
  let best: EngageTarget | null = null;
  let bestScore = Infinity;
  for (const e of enemies) {
    if (!enemyIsCombatRelevant(e)) continue;
    const d = dist(u.x, u.y, e.x, e.y);
    const reach = addAttackReach(u, e.radius);
    // Match Hit gate (`d < attackRange + radius`); tiny pad for edge float.
    if (d >= reach + 4) continue;
    const hpRatio = e.maxHp > 0 ? e.hp / e.maxHp : 1;
    const score = d + hpRatio * 28;
    if (score < bestScore) {
      bestScore = score;
      best = { x: e.x, y: e.y, isBoss: false, id: e.id };
    }
  }
  return best ?? bossAim;
}

/**
 * Among living hostiles, pick engage target by aggro (range-gated).
 * Commander focus → boss. All roles: adds only in melee/weapon hit band + boss
 * (hold tanks must not peel distant adds and lose boss AA / shield_bash).
 * When `includeBoss` is false (dormant boss / assault), never aim the boss.
 */
export function pickEngageTarget(
  u: RaidUnit,
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  role: RaidClassId,
  focusBoss: boolean,
  time = 0,
  includeBoss = true,
  allies: readonly RaidUnit[] = [],
  opts: EngagePickOpts = {},
): EngageTarget {
  if (focusBoss && includeBoss) {
    u.brain.targetId = -1;
    return { x: boss.x, y: boss.y, isBoss: true };
  }

  type Cand = { aim: EngageTarget; id: number };
  const peelPad =
    role === "tank"
      ? (raidCatalog.balance.combat.tankPeelPad ?? 4)
      : 4;
  const bondSticky = bondEngageSticky(u);
  const stickyLeash = bondSticky.stickyLeashPx;
  const committedId = u.brain.targetId >= 0 ? u.brain.targetId : -1;
  const dDisp = dispositionOf(u);
  // Polar 통찰↑: hesitate retarget (miss windows) — measure+selfProtect fold.
  const insight = 0.5 * dDisp.measure + 0.5 * dDisp.selfProtect;
  const minSwitch =
    bondSticky.minSwitchGain * insightRetargetHesitateMul(insight);
  const adjPriorityBonus = adjutantPriorityBonusOf(u);
  const priorityTargetId =
    adjPriorityBonus > 0.05 && allies.length > 0
      ? resolveEngagePriorityTargetId(allies, enemies, opts.facing)
      : null;
  const isBossAnchor =
    opts.bossAnchorTankId != null && u.id === opts.bossAnchorTankId;

  const cands: Cand[] = [];
  if (includeBoss) {
    const bossAim: EngageTarget = {
      x: boss.x,
      y: boss.y,
      isBoss: true,
      id: RAID_AGGRO_BOSS_ID,
    };
    cands.push({ aim: bossAim, id: RAID_AGGRO_BOSS_ID });
  }

  for (const e of enemies) {
    if (!enemyIsCombatRelevant(e)) continue;
    const d = dist(u.x, u.y, e.x, e.y);
    const reach = addAttackReach(u, e.radius);
    const inBand = d < reach + peelPad;
    // Low bond (personal): keep current target after push/knock even if briefly OOR.
    const stickyHold =
      e.id === committedId && stickyLeash > 0 && d < reach + peelPad + stickyLeash;
    if (!inBand && !stickyHold) continue;
    cands.push({
      aim: { x: e.x, y: e.y, isBoss: false, id: e.id },
      id: e.id,
    });
  }

  const nearestLiving = (): EngageTarget | null => {
    let best: RaidUnit | null = null;
    let bestD = Infinity;
    for (const e of enemies) {
      if (!enemyIsCombatRelevant(e)) continue;
      const d = dist(u.x, u.y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    if (!best) return null;
    return { x: best.x, y: best.y, isBoss: false, id: best.id };
  };

  if (cands.length === 0) {
    u.brain.engageRangeIds = [];
    const near = nearestLiving();
    if (near) {
      u.brain.targetId = near.id ?? -1;
      return near;
    }
    u.brain.targetId = -1;
    return { x: u.x, y: u.y - 1, isBoss: false };
  }

  const rangeIds = cands
    .filter((c) => {
      if (c.aim.isBoss) return false;
      const d = dist(u.x, u.y, c.aim.x, c.aim.y);
      const reach = addAttackReach(
        u,
        resolveAddRadius(enemies, c.id),
      );
      return d < reach + peelPad;
    })
    .map((c) => c.id)
    .sort((a, b) => a - b);
  const prevRange = u.brain.engageRangeIds ?? [];
  const newInRange = rangeIds.some((id) => !prevRange.includes(id));
  u.brain.engageRangeIds = rangeIds;

  const currentId =
    committedId >= 0
      ? committedId
      : includeBoss
        ? RAID_AGGRO_BOSS_ID
        : (cands[0]?.id ?? -1);
  // New hostile in weapon range: scale sticky by commit margin (continuum).
  // Low bond ≈ personal sticky; high bond ≈ open retarget — no binary <1 cliff.
  const commitMargin = bondSticky.stickyBonus + minSwitch;
  const openRetargetScale = newInRange
    ? Math.min(1, commitMargin / 20)
    : 1;
  const peelUrgency = peelInRangeBonus();
  // Boss-anchor soft floor — scales with bond priority obedience.
  const bossAnchorBonus = isBossAnchor
    ? Math.max(adjPriorityBonus, 40) * Math.max(0.15, dDisp.bond / 100)
    : 0;
  const pick = topThreatTarget(u.aggro, cands, {
    idOf: (c) => c.id,
    currentId,
    sticky: {
      stickyBonus: bondSticky.stickyBonus * openRetargetScale,
      minSwitchGain: minSwitch * openRetargetScale,
    },
    time,
    threatOf: (id, effective) =>
      id === RAID_AGGRO_BOSS_ID ? engageBossScore(effective) : effective,
    bonusOf: (c) => {
      let bonus = 0;
      if (c.aim.isBoss) {
        bonus += bossAnchorBonus;
        return bonus;
      }
      if (
        priorityTargetId != null &&
        c.id === priorityTargetId
      ) {
        bonus += adjPriorityBonus;
      }
      const d = dist(u.x, u.y, c.aim.x, c.aim.y);
      const reach = addAttackReach(u, resolveAddRadius(enemies, c.id));
      const inAa = d < reach + peelPad;
      // Leash-only current target mirrors peel score so knock OOR keeps sticky.
      if (inAa || (c.id === committedId && !rangeIds.includes(c.id))) {
        bonus += peelUrgency;
      }
      return bonus;
    },
  });
  if (!pick) {
    if (!includeBoss) {
      const near = nearestLiving();
      if (near) {
        u.brain.targetId = near.id ?? -1;
        return near;
      }
      u.brain.targetId = -1;
      return { x: u.x, y: u.y - 1, isBoss: false };
    }
    u.brain.targetId = -1;
    return { x: boss.x, y: boss.y, isBoss: true };
  }

  // Layer-2 Soft-safe focus among in-band cands only (no OOR chase).
  const bandIds = cands.map((c) => c.id);
  const kitFocus = resolveSoftSafeFocusId(
    u,
    includeBoss ? boss : null,
    enemies,
    bandIds,
    time,
  );
  if (kitFocus != null) {
    const focused = cands.find((c) => c.id === kitFocus);
    if (focused) {
      u.brain.targetId = focused.aim.isBoss ? -1 : (focused.aim.id ?? -1);
      return focused.aim.isBoss
        ? { x: boss.x, y: boss.y, isBoss: true }
        : focused.aim;
    }
  }

  u.brain.targetId = pick.aim.isBoss ? -1 : (pick.aim.id ?? -1);
  return pick.aim.isBoss
    ? { x: boss.x, y: boss.y, isBoss: true }
    : pick.aim;
}

/** Sticky wrap: aggro pick already includes sticky knobs. */
export function stickyEngageTarget(
  u: RaidUnit,
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  role: RaidClassId,
  focusBoss: boolean,
  time = 0,
  includeBoss = true,
  allies: readonly RaidUnit[] = [],
  opts: EngagePickOpts = {},
): EngageTarget {
  return pickEngageTarget(
    u,
    boss,
    enemies,
    role,
    focusBoss,
    time,
    includeBoss,
    allies,
    opts,
  );
}
